/**
 * What depends on the event date, and what has to go when it changes (DEV-7682, ST7).
 *
 * `00-03-EVENEMENT` is the SINGLE correction point for the date (the seven detail screens
 * only recall it read-only — see `utils/eventDateReminder.ts`). Coming back to it and
 * answering a DIFFERENT date invalidates everything the eligibility call produced AT THE
 * OLD DATE:
 *
 *  - `01-01-CONTRAT.eligibilityResponse` — the coverages in force that day AND the
 *    participants covered that day (`utils/beneficiarySource.ts` reads the latter),
 *  - `02-00-GARANTIE` — the coverage the claimant picked out of that response,
 *  - `02M-01-BENEFICIAIRE` / `02A-01-BENEFICIAIRE` / `02B-01-BENEFICIAIRE` — the people
 *    picked out of that response,
 *  - and — since the manual-testing feedback on this story — EVERYTHING ANSWERED AFTER
 *    THE COVERAGE PAGE: sections `03` (flow details), `04` (invoices, validation,
 *    supporting documents) and `05` (banking info, mandate). Those answers belong to the
 *    flow branched out of the OLD eligibility response; see
 *    `getScreensPurgedByEventDateChange` for why that is the same boundary as a coverage
 *    change.
 *
 * Keeping them would leave the claim asserting a coverage and people that a fresh
 * eligibility call at the new date might not return at all. So the date change warns, and
 * on confirmation purges everything from the coverage page onward PLUS the eligibility
 * verdict sitting on `01-01-CONTRAT` (`ELIGIBILITY_DERIVED_CONTRACT_KEYS`). What survives
 * is the contract and its travel dates (sections `00` and `01`) — exactly what survives a
 * coverage change.
 *
 * No call is made from `00-03-EVENEMENT`: the claimant necessarily walks back through
 * `01-01-CONTRAT`, whose "Continuer" AWAITS eligibility since ST8, so the re-check happens
 * there with the contract actually selected. Dropping the verdict here is what stops the
 * claimant from seeing the OLD date's answer in the meantime.
 *
 * An UNCHANGED date must trigger nothing — no warning, no purge, no call. Coming back to
 * this screen just to look at the answer is a normal move, and `hasEventDateChanged`
 * normalizes both sides (`formatDateShort`) so a stored ISO timestamp and a `yyyy-MM-dd`
 * field value do not read as a change.
 *
 * ADR-050 (multi-claim session, "submit another claim") is the second consumer: the set of
 * data reused from one claim to the next used to be `beneficiary + travel dates + contract`
 * and is now `travel dates + contract` only. The beneficiaries are not carried over — and
 * they cannot be, since the next claim has no event date yet to filter them on. See
 * `dropDataReusedAcrossClaims`.
 *
 * PII (ADR-039): this module moves beneficiary-bearing structures around but never reads a
 * field of them and never logs.
 */

import { formatDateShort } from './dateUtils';
import { EVENT_DATE_SCREEN_CODE } from './eventDateScreenVisibility';
import { CONTRACT_SCREEN_CODE } from './beneficiarySource';

/** The coverage chosen out of the eligibility response. */
export const CLAIM_TYPE_SCREEN_CODE = '02-00-GARANTIE';

/** One per flow — the people chosen out of the eligibility response. */
export const BENEFICIARY_SCREEN_CODES = [
  '02M-01-BENEFICIAIRE',
  '02A-01-BENEFICIAIRE',
  '02B-01-BENEFICIAIRE'
] as const;

/**
 * Screens whose answers were derived DIRECTLY from the eligibility call made at the OLD
 * date. Purged together — the coverage and the people come from the same response.
 *
 * This is NOT the purge scope of a date change (see
 * `getScreensPurgedByEventDateChange`): it is the "reused across claims" exclusion list
 * of ADR-050, where sections `03`+ have already been dropped by
 * `clearClaimFromSection('02')`.
 */
export const EVENT_DATE_DERIVED_SCREEN_CODES: readonly string[] = [
  CLAIM_TYPE_SCREEN_CODE,
  ...BENEFICIARY_SCREEN_CODES
];

/**
 * First section an event-date change invalidates: `02`, the coverage page.
 *
 * Same boundary as `claimStore.clearClaimFromSection('02')` — sections `00` and `01`
 * (the contract and its Parent Context trips ⇒ the travel dates) survive, everything
 * from the coverage page onward does not.
 */
export const EVENT_DATE_PURGED_FROM_SECTION = '02';

/** Leading section number of a screen code (`'02M-01-BENEFICIAIRE'` ⇒ `2`). */
const sectionOf = (screenCode: string): number =>
  Number.parseInt(screenCode.split('-')[0], 10);

/**
 * Is this screen's answer invalidated by an event-date change?
 *
 * Section `>= 02`. `NaN` (an unrecognizable key) is KEPT — same "unknown screen, keep it
 * to be safe" stance as `utils/flowDataCleanup.ts`.
 */
export const isPurgedByEventDateChange = (screenCode: string): boolean => {
  const section = sectionOf(screenCode);

  return !Number.isNaN(section) && section >= Number.parseInt(EVENT_DATE_PURGED_FROM_SECTION, 10);
};

/**
 * The screens a CONFIRMED date change deletes, out of the ones this claim actually holds.
 *
 * WHY THE WHOLE OF SECTIONS `02`+ AND NOT JUST THE DERIVED SCREENS (DEV-7682, manual
 * testing feedback #4): the coverage page is where the claim branches. Re-running
 * eligibility at a new date can return a different set of coverages, so the claimant may
 * well land on a different flow — and every answer given after `02-00-GARANTIE` belongs
 * to the flow chosen out of the OLD response. Keeping them would leave a medical claim
 * carrying cancellation invoices, or details attached to a coverage that no longer
 * exists.
 *
 * ALIGNMENT WITH THE COVERAGE CHANGE — the reference behaviour is
 * `claimStore.cleanStepDataForFlow` (via `checkFlowChange`), fired from
 * `ClaimTypeScreen.handleConfirmFlowChange`. That one drops the flow-specific screens of
 * the abandoned flow and, by construction (`COMMON_SCREENS`), never touches sections `00`
 * / `01`. This scope is a deliberate SUPERSET of it: a date change invalidates the
 * eligibility verdict ITSELF, not just the branch taken out of it, so it also drops the
 * screens both flows share (`04-01-FACTURES`, `04-02-VALIDATION`,
 * `04-03-JUSTIFICATIFS`, `05-01-PAIEMENT`, …). The superset relation is pinned by a test
 * so the two rules cannot drift apart silently.
 *
 * PII (ADR-039): returns screen CODES only, never a value read out of them.
 */
export const getScreensPurgedByEventDateChange = (
  stepData: ClaimStepData | null | undefined
): string[] => Object.keys(stepData ?? {}).filter(isPurgedByEventDateChange);

/**
 * Keys of `stepData['01-01-CONTRAT']` written from the eligibility response.
 * Everything NOT listed here identifies the contract itself (`selectedContract`,
 * `contractNumber`, `contractDetails` and its Parent Context trips) and survives: the
 * contract stays, only its date-dependent verdict is dropped.
 *
 * TWO consumers, ONE list — deliberately:
 *  - ADR-050 "submit another claim" (`dropDataReusedAcrossClaims` below),
 *  - a confirmed event-date change (`EventDateScreen.handleConfirmDateChange`, via
 *    `claimStore.clearScreenDataKeys`).
 * Both drop the verdict for exactly the same reason — it was computed at a date that no
 * longer applies. A second copy of this list would let the two rules drift apart.
 */
export const ELIGIBILITY_DERIVED_CONTRACT_KEYS: readonly string[] = [
  'eligibilityResponse',
  'productType',
  'benefits',
  'hasMedicalCoverage',
  'hasCancellationCoverage',
  'hasBaggageCoverage',
  'eligibilityServiceUnavailable',
  'eligibilityLoading',
  'noBenefitsFound'
];

/** `stepData` as this module reads it — loose on siblings, same convention as its neighbours. */
export type ClaimStepData = Record<string, unknown>;

/**
 * Did the claimant actually answer a different date?
 *
 * Both sides go through `formatDateShort` first: the stored value can be a full ISO
 * timestamp coming from the Parent Context while the field always holds `yyyy-MM-dd`, and
 * those two must compare equal. No date is ever built with `new Date(iso)` here — that
 * parses as UTC and shifts the day backwards in every North-American timezone.
 *
 * A claim with no previous answer returns `false`: a first answer derives nothing and
 * invalidates nothing.
 */
export const hasEventDateChanged = (
  previousEventDate: string | null | undefined,
  nextEventDate: string | null | undefined
): boolean => {
  const previous = formatDateShort(previousEventDate);
  const next = formatDateShort(nextEventDate);

  return previous !== '' && next !== '' && previous !== next;
};

/** Does this key hold something worth purging? (`{}` / `[]` / `''` do not.) */
const hasContent = (value: unknown): boolean => {
  if (value === null || value === undefined || value === '') return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
};

/**
 * Does this claim carry anything derived from its current event date?
 *
 * Guards the warning: changing the date on a claim that never reached the eligibility call
 * has nothing to lose, and a modal announcing deletions that will not happen is noise —
 * same guard as the contract-change warning on `01-01-CONTRAT`, which only warns when
 * there IS beneficiary data to drop.
 *
 * Read off `getScreensPurgedByEventDateChange`, deliberately: the guard and the purge
 * must answer the same question, or the claimant gets warned about deletions that do not
 * happen (or worse, loses uploaded invoices with no warning at all).
 */
export const hasEventDateDerivedData = (stepData: ClaimStepData | null | undefined): boolean => {
  if (!stepData) return false;

  const contractData = stepData[CONTRACT_SCREEN_CODE] as
    | { eligibilityResponse?: unknown }
    | undefined;

  return (
    hasContent(contractData?.eligibilityResponse) ||
    getScreensPurgedByEventDateChange(stepData).some(screenCode => hasContent(stepData[screenCode]))
  );
};

/** Copy of `screenData` without the keys the eligibility response wrote. */
const withoutEligibilityVerdict = (screenData: unknown): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries((screenData ?? {}) as Record<string, unknown>).filter(
      ([key]) => !ELIGIBILITY_DERIVED_CONTRACT_KEYS.includes(key)
    )
  );

/**
 * ADR-050 — the data a NEW claim of the same session reuses from the previous one.
 *
 * Pure: takes the `stepData` already filtered by `clearClaimFromSection` (sections `00`
 * and `01` only) and drops what must NOT be carried over:
 *  - `00-03-EVENEMENT`, so the next claim asks its own date instead of silently inheriting
 *    the previous event's one — two claims of the same trip are two distinct events,
 *  - the eligibility verdict on `01-01-CONTRAT`, because it was computed at that dropped
 *    date; its `beneficiaries` are exactly the list `utils/beneficiarySource.ts` would
 *    prefill, which is how "beneficiary" leaves the reused set,
 *  - the derived screens, already outside sections `00`/`01` but listed for the day this
 *    helper is called on an unfiltered `stepData`.
 *
 * What stays: the contract (number, details, Parent Context trips ⇒ travel dates) and the
 * claimant. Eligibility is re-queried at the new date when the claimant walks back through
 * `01-01-CONTRAT`.
 */
export const dropDataReusedAcrossClaims = <T extends ClaimStepData>(stepData: T): T => {
  const kept: ClaimStepData = {};

  Object.entries(stepData).forEach(([screenCode, screenData]) => {
    if (screenCode === EVENT_DATE_SCREEN_CODE) return;
    if (EVENT_DATE_DERIVED_SCREEN_CODES.includes(screenCode)) return;

    kept[screenCode] =
      screenCode === CONTRACT_SCREEN_CODE ? withoutEligibilityVerdict(screenData) : screenData;
  });

  // Same shape minus a few keys — the callers keep the `stepData` type they came with.
  return kept as T;
};
